import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "../lib/scrollLock";

/**
 * A modal. Over the page, never in it.
 *
 * Jonathan, 2026-09-15: *"I don't like expanding pages when everything jumps
 * away ... It opens a modal."* Everything that used to unfold inside the page
 * -- the group editor, a person's access -- opens here instead, so the page
 * underneath keeps its shape and its scroll position.
 *
 * Escape closes. The backdrop closes. The page behind stops scrolling while
 * one is open. Focus lands on the panel so the keyboard is already in it.
 */
export default function Modal({
  title,
  onClose,
  children,
  wide = false,
  actions,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Two columns of content want the wide panel. */
  wide?: boolean;
  /** The buttons along the bottom, right-aligned. */
  actions?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Page stays put behind it (lib/scrollLock.ts: lock <html>, never <body>).
  useScrollLock();

  useEffect(() => {
    panel.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`modal ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={panel}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {actions ? <div className="modal-actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A section that opens and closes, for a list too long to show whole.
 * The head carries the count so a closed section still says what is in it.
 */
export function Fold({
  label,
  summary,
  open,
  onToggle,
  children,
  trailing,
}: {
  label: ReactNode;
  summary?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** A control on the head's right that must not toggle the fold. */
  trailing?: ReactNode;
}) {
  return (
    <div className={`fold ${open ? "open" : ""}`}>
      <div className="fold-head">
        <button type="button" className="fold-toggle" onClick={onToggle} aria-expanded={open}>
          <span className="fold-caret" aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
          <span className="fold-label">{label}</span>
          {summary ? <span className="fold-summary">{summary}</span> : null}
        </button>
        {trailing ? <span className="fold-trailing">{trailing}</span> : null}
      </div>
      {open ? <div className="fold-body">{children}</div> : null}
    </div>
  );
}
